import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useDebouncedValue from 'hooks/useDebouncedValue';
import { GetJobsParams, GetJobsSortParams, JobStatuses } from 'store/jobs/job';
import { SortDirection } from 'utils/tableHelpers';
import useIndividuals from 'hooks/useIndividuals';
import useCustomers from 'hooks/useCustomers';
import useTeams from 'hooks/useTeams';

const DATE_FORMAT = 'YYYY-MM-DD';

export enum JobAppliedParams {
    QUERY = 'query',
    DATE_RANGE = 'dateRange',
    EXACT_DATE = 'exactDate',
    STATUS = 'jobStatus',
    CUSTOMER_ID = 'customerId',
    OWNER_ID = 'ownerId',
    CREATED_BY = 'createdById',
    TEAM_ID = 'teamId',
    INCOTERM_CODE = 'incotermCode',
}

export type FilterTag = {
    key: JobAppliedParams;
    label: string;
    value: string;
};

export enum JobParamsKeys {
    SORT_DIR = 'sortDirection',
    SORT = 'sort',
    PAGE = 'page',
    SIZE = 'size',
    CUSTOMER_ID = 'customerId',
    TEAM_ID = 'teamId',
    JOB_STATUS = 'jobStatus',
    ARCHIVED = 'archived',
    START_DATE = 'startDate',
    END_DATE = 'endDate',
    EXACT_DATE = 'exactDate',
    QUERY = 'query',
    OWNER_ID = 'ownerId',
    CREATED_BY = 'createdById',
    INCOTERM_CODE = 'incotermCode',
}

export type DateRange = { startDate?: string; endDate?: string };

const useJobsParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { getIndividual, entities: individualEntities } = useIndividuals();
    const { getCustomer, entities: customerEntities } = useCustomers();
    const { getTeamById, entities: teamEntities } = useTeams();

    const initialQuery = searchParams.get(JobParamsKeys.QUERY) ?? '';
    const initialExactDate = searchParams.get(JobParamsKeys.EXACT_DATE) || undefined;
    const initialStart = searchParams.get(JobParamsKeys.START_DATE) || undefined;
    const initialEnd = searchParams.get(JobParamsKeys.END_DATE) || undefined;
    const initialSort = (searchParams.get(JobParamsKeys.SORT) as GetJobsSortParams) || GetJobsSortParams.CREATED_AT;
    const initialSortDir = (searchParams.get(JobParamsKeys.SORT_DIR) as 'ASC' | 'DESC') || 'DESC';
    const initialPage = Number(searchParams.get(JobParamsKeys.PAGE)) || 0;
    const initialSize = Number(searchParams.get(JobParamsKeys.SIZE)) || 10;
    const archivedParam = searchParams.get(JobParamsKeys.ARCHIVED);
    const initialArchived = archivedParam == null ? undefined : archivedParam === 'true';
    const initialStatuses = (searchParams.getAll(JobParamsKeys.JOB_STATUS) as JobStatuses[]) || undefined;
    const initialCustomerId = searchParams.get(JobParamsKeys.CUSTOMER_ID) ?? undefined;
    const initialOwnerId = searchParams.get(JobParamsKeys.OWNER_ID) ?? undefined;
    const initialCreatedById = searchParams.get(JobParamsKeys.CREATED_BY) ?? undefined;
    const initialTeamId = searchParams.get(JobParamsKeys.TEAM_ID) ?? undefined;
    const initialIncotermCode = searchParams.get(JobParamsKeys.INCOTERM_CODE) ?? undefined;

    const [queryInputVal, setQueryInputVal] = useState(initialQuery);
    const [dates, setDates] = useState<DateRange>({ startDate: initialStart, endDate: initialEnd });
    const [page, setPage] = useState<number>(initialPage);
    const [size, setSize] = useState<number>(initialSize);
    const [archived, setArchived] = useState<boolean | undefined>(initialArchived);
    const [exactDate, setExactDate] = useState<string | undefined>(initialExactDate);
    const [statuses, setStatuses] = useState<JobStatuses[]>(initialStatuses);
    const [customerId, setCustomerId] = useState<string | undefined>(initialCustomerId);
    const [customerLabel, setCustomerLabel] = useState<string | undefined>(undefined);
    const [ownerId, setOwnerId] = useState<string | undefined>(initialOwnerId);
    const [ownerLabel, setOwnerLabel] = useState<string | undefined>(undefined);
    const [createdById, setCreatedById] = useState<string | undefined>(initialCreatedById);
    const [createdByLabel, setCreatedByLabel] = useState<string | undefined>(undefined);
    const [teamId, setTeamId] = useState<string | undefined>(initialTeamId);
    const [teamName, setTeamName] = useState<string | undefined>(undefined);
    const [incotermCode, setIncotermCode] = useState<string | undefined>(initialIncotermCode);

    const [sortState, setSortState] = useState<{
        sort: GetJobsSortParams | undefined;
        sortDirection: SortDirection | undefined;
    }>({ sort: initialSort, sortDirection: initialSortDir });

    const debouncedQuery = useDebouncedValue(queryInputVal, 300);

    const writeParams = useCallback(
        (next: Partial<GetJobsParams>) => {
            const searchParam = new URLSearchParams(searchParams.toString());

            const apply = (key: string, val?: string | null) => {
                if (val === undefined || val === null || val === '') {
                    searchParam.delete(key);
                } else {
                    searchParam.set(key, String(val));
                }
            };

            const applyArray = (key: string, values?: (string | number)[] | null) => {
                searchParam.delete(key);
                if (values && values.length) {
                    values?.forEach((v) => searchParam.append(key, String(v)));
                }
            };

            apply(JobParamsKeys.QUERY, next.query);
            apply(JobParamsKeys.START_DATE, next.startDate);
            apply(JobParamsKeys.END_DATE, next.endDate);
            apply(JobParamsKeys.EXACT_DATE, next.exactDate);
            apply(JobParamsKeys.SORT, next.sort);
            apply(JobParamsKeys.SORT_DIR, next.sortDirection);
            apply(JobParamsKeys.PAGE, next?.page?.toString());
            apply(JobParamsKeys.SIZE, next?.size?.toString());
            apply(JobParamsKeys.CUSTOMER_ID, next.customerId);
            apply(JobParamsKeys.OWNER_ID, next.ownerId);
            apply(JobParamsKeys.CREATED_BY, next.createdById);
            apply(JobParamsKeys.TEAM_ID, next.teamId);
            apply(JobParamsKeys.INCOTERM_CODE, next.incotermCode);
            apply(JobParamsKeys.ARCHIVED, next.archived === undefined ? undefined : String(next.archived));
            applyArray(JobParamsKeys.JOB_STATUS, next.jobStatus as JobStatuses[] | undefined);

            setSearchParams(searchParam, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        writeParams({
            query: debouncedQuery,
            startDate: dates.startDate,
            endDate: dates.endDate,
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
            page,
            size,
            archived,
            exactDate,
            jobStatus: statuses?.length ? (statuses as JobStatuses[]) : undefined,
            customerId,
            ownerId,
            createdById,
            teamId,
            incotermCode,
        });
    }, [
        writeParams,
        debouncedQuery,
        dates.startDate,
        dates.endDate,
        sortState.sort,
        sortState.sortDirection,
        page,
        size,
        archived,
        exactDate,
        statuses,
        customerId,
        ownerId,
        createdById,
        teamId,
        incotermCode,
    ]);

    useEffect(() => {
        if (!customerId) {
            setCustomerLabel(undefined);
            return;
        }

        const customer = customerEntities[customerId];

        if (!customer) {
            getCustomer(customerId).catch(() => {
                setCustomerLabel(undefined);
            });
            return;
        }

        setCustomerLabel(customer.name);
    }, [customerEntities, customerId, getCustomer]);

    useEffect(() => {
        if (!ownerId) {
            setOwnerLabel(undefined);
            return;
        }

        const ind = individualEntities[ownerId];

        if (!ind) {
            getIndividual(ownerId).catch(() => setOwnerLabel(undefined));
            return;
        }

        setOwnerLabel(ind?.fullName ?? ind?.email);
    }, [individualEntities, ownerId, getIndividual]);

    useEffect(() => {
        if (!createdById) {
            setCreatedByLabel(undefined);
            return;
        }

        const ind = individualEntities[createdById];

        if (!ind) {
            getIndividual(createdById).catch(() => setCreatedByLabel(undefined));
            return;
        }

        setCreatedByLabel(ind?.fullName ?? ind?.email);
    }, [individualEntities, createdById, getIndividual]);

    useEffect(() => {
        if (!teamId) {
            setTeamName(undefined);
            return;
        }

        const team = teamEntities[teamId];

        if (!team) {
            getTeamById(teamId).catch(() => setTeamName(undefined));
            return;
        }
        setTeamName(team?.name);
    }, [getTeamById, teamEntities, teamId]);

    const appliedParams = useMemo(
        () => ({
            query: debouncedQuery || undefined,
            startDate: dates.startDate,
            endDate: dates.endDate,
            sort: sortState.sort,
            sortDirection: sortState.sortDirection,
            page,
            size,
            exactDate,
            archived,
            jobStatus: statuses,
            customerId,
            ownerId,
            createdById,
            teamId,
            incotermCode,
        }),
        [
            debouncedQuery,
            dates.startDate,
            dates.endDate,
            sortState.sort,
            sortState.sortDirection,
            page,
            size,
            exactDate,
            archived,
            statuses,
            customerId,
            ownerId,
            createdById,
            teamId,
            incotermCode,
        ]
    );

    const tags: FilterTag[] = useMemo(() => {
        const list: FilterTag[] = [];

        const { startDate, endDate } = dates;
        if (startDate && endDate) {
            list.push({
                key: JobAppliedParams.DATE_RANGE,
                label: 'Date',
                value: `${moment(startDate).format(DATE_FORMAT)} - ${moment(endDate).format(DATE_FORMAT)}`,
            });
        }

        if (exactDate) {
            list.push({
                key: JobAppliedParams.EXACT_DATE,
                label: 'Date',
                value: moment(exactDate).format(DATE_FORMAT),
            });
        }

        if (statuses.length) {
            for (let i = 0; i < statuses.length; i++) {
                const status = statuses[i];
                list.push({
                    key: JobAppliedParams.STATUS,
                    label: 'Job Status',
                    value: status,
                });
            }
        }

        if (customerId) {
            list.push({
                key: JobAppliedParams.CUSTOMER_ID,
                label: 'Customer',
                value: customerLabel ?? 'Unknown Customer',
            });
        }

        if (ownerId) {
            list.push({
                key: JobAppliedParams.OWNER_ID,
                label: 'Assigned To',
                value: ownerLabel ?? 'Unknown User',
            });
        }

        if (createdById) {
            list.push({
                key: JobAppliedParams.CREATED_BY,
                label: 'Created By',
                value: createdByLabel ?? 'Unknown User',
            });
        }

        if (teamId) {
            list.push({
                key: JobAppliedParams.TEAM_ID,
                label: 'Team',
                value: teamName ?? 'Unknown Team',
            });
        }

        if (incotermCode) {
            list.push({
                key: JobAppliedParams.INCOTERM_CODE,
                label: 'Delivery Terms',
                value: incotermCode,
            });
        }

        return list;
    }, [
        dates,
        exactDate,
        statuses,
        customerId,
        ownerId,
        createdById,
        teamId,
        customerLabel,
        ownerLabel,
        createdByLabel,
        teamName,
        incotermCode,
    ]);

    const clearFilter = useCallback((key: string, value?: string) => {
        switch (key) {
            case JobAppliedParams.QUERY: {
                setQueryInputVal('');
                break;
            }

            case JobAppliedParams.DATE_RANGE: {
                setDates({
                    startDate: undefined,
                    endDate: undefined,
                });
                break;
            }

            case JobAppliedParams.EXACT_DATE: {
                setExactDate(undefined);
                break;
            }

            case JobAppliedParams.STATUS: {
                if (value) {
                    setStatuses((prev) => prev.filter((s) => s !== value));
                } else {
                    setStatuses([]);
                }
                break;
            }

            case JobAppliedParams.CUSTOMER_ID: {
                setCustomerId(undefined);
                break;
            }

            case JobAppliedParams.OWNER_ID: {
                setOwnerId(undefined);
                break;
            }

            case JobAppliedParams.CREATED_BY: {
                setCreatedById(undefined);
                break;
            }

            case JobAppliedParams.TEAM_ID: {
                setTeamId(undefined);
                break;
            }

            case JobAppliedParams.INCOTERM_CODE: {
                setIncotermCode(undefined);
                break;
            }

            default: {
                break;
            }
        }
    }, []);

    const clearAll = useCallback(() => {
        setPage(0);
        setDates({
            startDate: undefined,
            endDate: undefined,
        });
        setExactDate(undefined);
        setQueryInputVal('');
        setStatuses([]);
        setCustomerId(undefined);
        setOwnerId(undefined);
        setCreatedById(undefined);
        setTeamId(undefined);
        setIncotermCode(undefined);
    }, []);

    return {
        queryInputVal,
        tags,
        dates,
        appliedParams,
        debouncedQuery,
        sortState,
        exactDate,
        setQueryInputVal,
        setDates,
        setSortState,
        clearFilter,
        clearAll,
        setArchived,
        setSize,
        setPage,
        setCustomerId,
        setOwnerId,
        setExactDate,
        setStatuses,
        setCreatedById,
        setTeamId,
        setIncotermCode,
    };
};

export default useJobsParams;
